import React, {
  FC,
  forwardRef,
  ForwardRefRenderFunction,
  Fragment,
  MutableRefObject,
  ReactNode,
  RefObject,
} from 'react';
import {Link} from 'react-router-dom';

import {
  BetData,
  Card,
  EventStatus,
  GenericProps,
  MatchInfo,
  MatchLiveData,
  MatchScorer,
  MatchScorers,
  PeriodScore,
  Sport,
} from 'utils/types';

import './styles.scss';

import icon_cardr from 'assets/svg/match_timeline_icon_card-r.svg';
import icon_cardyr from 'assets/svg/match_timeline_icon_card-yr.svg';
import icon_goal from 'assets/svg/match_header_icon_goal.svg';
import teamHomeLogoFallback from 'assets/img/team-home-logo-placeholder.png'; // TODO: svg and colorize
import teamAwayLogoFallback from 'assets/img/team-away-logo-placeholder.png'; // TODO: svg and colorize

import useBreakpointLG from 'hooks/useBreakpointLG';
import useBreakpointXL from 'hooks/useBreakpointXL';
import useCompetitionSponsor from 'hooks/useCompetitionSponsor';
import WidgetBet from '../WidgetBet';
import BetButton from '../BetButton';
import LiveBroadcastPlayer from '../LiveBroadcastPlayer';
import StoiximanLogo from "../../../assets/svg/StoiximanOFFWHITE21plus.svg";
import NovibetLogo from "../../../assets/img/novibet-logo-mundial.png"

// BIQ: split file

const getFormattedDate = (date: Date): string => {
  return `${date.getDate().toString().padStart(2, '0')}.${(date.getMonth() + 1)
    .toString()
    .padStart(2, '0')}.${date.getFullYear()}`;
};

const getFormattedTime = (date: Date): string => {
  return `${date.getHours().toString().padStart(2, '0')}:${date
    .getMinutes()
    .toString()
    .padStart(2, '0')}`;
};

interface ScorerProps extends GenericProps {
  scorer: MatchScorer;
}

const Scorer: FC<ScorerProps> = ({scorer: {name, goals, card}, className}: ScorerProps) => (
  <p key={name} className={`HeaderInfo__scorer ${className}`}>
    {`${name} ${goals?.map(min => `${min}`).join(', ')}`}
    {card === Card.RED_CARD && (
      <img
        className="HeaderInfo__scorerIcon HeaderInfo__scorerIcon--card"
        src={icon_cardr}
        alt="Κόκκινη κάρτα"
      />
    )}
    {card === Card.YELLOW_RED_CARD && (
      <img
        className="HeaderInfo__scorerIcon HeaderInfo__scorerIcon--card"
        src={icon_cardyr}
        alt="Διπλή κίτρινη κάρτα"
      />
    )}
    {!card && (
      <img
        className="HeaderInfo__scorerIcon HeaderInfo__scorerIcon--goal"
        src={icon_goal}
        alt="Γκολ"
      />
    )}
  </p>
);

const ScorersPanel: FC<{scorers: MatchScorers}> = ({scorers}: {scorers: MatchScorers}) => {
  if (
    scorers &&
    (('home' in scorers && !!scorers.home.length) || ('away' in scorers && !!scorers.away.length))
  ) {
    return (
      <div className="HeaderInfo__scorers">
        {'home' in scorers && (
          <div className="HeaderInfo__scorersList">
            {scorers.home.map(scorer => (
              <Scorer key={scorer.name} scorer={scorer} className="HeaderInfo__scorer--home" />
            ))}
          </div>
        )}
        {'away' in scorers && (
          <div className="HeaderInfo__scorersList">
            {scorers.away.map(scorer => (
              <Scorer key={scorer.name} scorer={scorer} className="HeaderInfo__scorer--away" />
            ))}
          </div>
        )}
      </div>
    );
  }
  return null;
};

const PeriodScoresPanel: FC<{periodScores: PeriodScore[]; sport: Sport; id: string}> = ({
  periodScores,
  sport,
  id,
}: {
  periodScores: PeriodScore[];
  sport: Sport;
  id: string;
}) => {
  if (periodScores && periodScores.length) {
    return (
      <div className="HeaderInfo__periodScores PeriodScores">
        {periodScores.map(
          ps =>
            ps.homeScore !== undefined &&
            ps.awayScore !== undefined && (
              <Fragment key={`${id}-${ps.periodNumber}-${ps.periodType}`}>
                <h4 className="PeriodScores__period">
                  {ps.periodType === 'ΠΑΡΑΤΑΣΗ'
                    ? `ΠΑΡΑΤΑΣΗ ${ps.periodNumber}`
                    : `${(sport === Sport.TENNIS || sport === Sport.VOLLEYBALL) ? 'ΣΕΤ' : 'ΠΕΡΙΟΔΟΣ'} ${ps.periodNumber}`}
                </h4>
                <p className="PeriodScores__score">{`${ps.homeScore} : ${ps.awayScore}`}</p>
              </Fragment>
            )
        )}
      </div>
    );
  }
  return null;
};

interface Props {
  matchInfo: MatchInfo;
  scorers: MatchScorers;
  liveData: MatchLiveData;
  betData?: BetData;
  // betLink?: string | null; 
}

// eslint-disable-next-line react/display-name
const HeaderInfo = forwardRef<HTMLElement, Props>(
  (
    {
      matchInfo: {srId, sport, competition, seasonCompetitionId, grouping, date, teams, location, customLocation, tweetHashTag, providerView},
      scorers,
      liveData: {scores, status, matchStatus, minute, stoppageTime, liveBroadcast, periodScores},
      //  betLink, 
      betData,
    }: Props,
    ref: ((instance: HTMLElement | null) => void) | MutableRefObject<HTMLElement | null> | null
  ) => {
    const isBreakpointLG = useBreakpointLG();
    const isBreakpointXL = useBreakpointXL();
    const locationSR = location;

    const { showStoiximanBadge, showNovibetBadge, showNovibetBadgeEuroleague } = useCompetitionSponsor({ sport, seasonCompetitionId, providerView, isBreakpointXL, teams });
    
    // eslint-disable-next-line no-template-curly-in-string
    const novibetImpressionURL = "https://ad.doubleclick.net/ddm/trackimp/N2192408.152588SPORT24.GR/B34865947.457054466;dc_trk_aid=651432883;dc_trk_cid=247938592;ord=[timestamp];dc_lat=;dc_rdid=;tag_for_child_directed_treatment=;tfua=;gdpr=${GDPR};gdpr_consent=${GDPR_CONSENT_755};ltd=;dc_tdv=1?";
    
    const novibetClickUrl = "https://ad.doubleclick.net/ddm/trackclk/N2192408.152588SPORT24.GR/B34865947.457054466;dc_trk_aid=651432883;dc_trk_cid=247938592;dc_lat=;dc_rdid=;tag_for_child_directed_treatment=;tfua=;gdpr=$%7BGDPR%7D;gdpr_consent=$%7BGDPR_CONSENT_755%7D;ltd=;dc_tdv=1"

    return (
      <section className={`HeaderInfo HeaderInfo--${sport.toLowerCase()}`} ref={ref}>
        <div className="HeaderInfo__matchInfo">
          <span className="HeaderInfo__matchDate">{`${getFormattedDate(date)}${
            isBreakpointLG ? ` - ${getFormattedTime(date)}` : ''
          }`}</span>
          <span className="HeaderInfo__competition">
            {competition}
            {grouping && (
              <>
                <br />
                {grouping}
              </>
            )}
          </span>
          <div className="HeaderInfo__team">
            <img
              // src={teams.home.imageUrl || teamHomeLogoFallback}
              src={
                sport === Sport.TENNIS
                  ? teamHomeLogoFallback
                  : teams.home.imageUrl || teamHomeLogoFallback
              }
              className="HeaderInfo__teamLogo"
              alt={teams.home.name}
            />
            <span className="HeaderInfo__teamName">
              {isBreakpointLG ? teams.home.name : teams.home.shortName}
            </span>
          </div>
          <span className="HeaderInfo__score HeaderInfo__score--home">{scores.home ?? '-'}</span>
          <span className="HeaderInfo__score HeaderInfo__score--separator">:</span>
          <span className="HeaderInfo__score HeaderInfo__score--away">{scores.away ?? '-'}</span>
          <div className="HeaderInfo__team">
            <img
              // src={teams.away.imageUrl || teamAwayLogoFallback}
              src={
                sport === Sport.TENNIS
                  ? teamAwayLogoFallback
                  : teams.away.imageUrl || teamAwayLogoFallback
              }
              className="HeaderInfo__teamLogo"
              alt={teams.away.name}
            />
            <span className="HeaderInfo__teamName">
              {isBreakpointLG ? teams.away.name : teams.away.shortName}
            </span>
          </div>
          {
            /* eslint-disable no-nested-ternary */
            status === EventStatus.NOT_STARTED ? (
              !isBreakpointLG && (
                <span className="HeaderInfo__status">{getFormattedTime(date)}</span>
              )
            ) : status === EventStatus.LIVE &&
              ![Sport.TENNIS, Sport.VOLLEYBALL].includes(sport) &&
              matchStatus !== 'ΗΜΙ' &&
              matchStatus !== 'BREAK' &&
              minute !== 0 ? (
              <>
                <span className="HeaderInfo__status">{minute}{
                  stoppageTime ? ` + ${stoppageTime}` : ''
                }&apos;</span>
                {![Sport.SOCCER, Sport.HANDBALL].includes(sport) && (
                  <PeriodScoresPanel periodScores={periodScores} sport={sport} id={srId} />
                )}
              </>
            ) : (
              <>
                <span className="HeaderInfo__status">{matchStatus}</span>
                {![Sport.SOCCER, Sport.HANDBALL].includes(sport) && (
                  <PeriodScoresPanel periodScores={periodScores} sport={sport} id={srId} />
                )}
              </>
            )
          }
        </div>
        {[Sport.SOCCER].includes(sport) && <ScorersPanel scorers={scorers} />}
        <div className="HeaderInfo__location">{(customLocation !== '') ? (customLocation != null ? customLocation : locationSR) : locationSR}</div> 
        {!isBreakpointXL && betData?.odds && betData.odds.length > 0 && (providerView==="All" || providerView==="Matchcenter") && (status === EventStatus.NOT_STARTED || status === EventStatus.LIVE) && (
          <WidgetBet className="HeaderInfo__bet" betData={betData} collapsible />
        )}
        {showStoiximanBadge && !showNovibetBadgeEuroleague && (
          <div className="WidgetBet__provider sponsored">

            <span className="WidgetBet__providerLabel">POWERED BY</span>
            <a
              className="WidgetBet__providerLogo"
              href={`${betData?.provider?.clickUrl || "	https://stx-track.com/C.ashx?btag=a_232b_1015003c_&affid=95&siteid=232&adid=1015003&c="}`}
              target="_blank"
              rel="external noopener noreferrer"
            >
              <img src={StoiximanLogo} alt="Stoiximan"/>
            </a>
          </div>
        )}
        {(showNovibetBadge || showNovibetBadgeEuroleague) && (
          <div className="WidgetBet__provider sponsored">

            <span className="WidgetBet__providerLabel">POWERED BY</span>
            <a
              className="WidgetBet__providerLogo"
              href={novibetClickUrl}
              target="_blank"
              rel="external noopener noreferrer"
            >
              {showNovibetBadgeEuroleague && (
                  <img src={novibetImpressionURL}
                    alt="impression"
                    style={{opacity: '0', position: 'absolute', height: '1px', width: '1px'}}
                  />
              )}
              <img className='novibet' src={NovibetLogo} alt="Novibet"/>
            </a>
          </div>
        )}
        
        {tweetHashTag && (
          <a
            className="HeaderInfo__twitter"
            target="_blank"
            rel="noreferrer"
            href={`https://twitter.com/intent/tweet?hashtags=${encodeURIComponent(
              tweetHashTag?.replace('#', '')
            )}`}
          >
            {tweetHashTag}
          </a>
        )}

        {liveBroadcast?.activity && <LiveBroadcastPlayer liveBroadcastParams={liveBroadcast}/>}
        {/* {betLink && (providerView==="All" || providerView==="Matchcenter") && <BetButton link={betLink} />} */}
      </section>
    );
  }
);

export default HeaderInfo;
